/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo } from "react";

// antd:
// import { LoadingOutlined } from "@ant-design/icons";
import { Badge } from "antd";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
// Redux-hooks:
import { useAppSelector } from "../../libs/redux-hooks.ts"; // useAppDispatch
// Reducers:
import { getElementState, getTheme, getLng, getUser } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

type Tbadge = {
  size:"default" | "small",
  status?:"default" | "success" | "error" | "processing" | "warning",
  dot?:boolean,
  ribbon?:boolean,
  placement?:"start" | "end"
}

const { rz } = window;

const RZbadge:FC<TuniversalElementProps> = ({attributes, children}) => {
  RZbadge.displayName = "Badge";
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);
  const lng = useAppSelector(getLng);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const elState = useAppSelector(getElementState(rootAttributes.key, fullPath)); // value, visible, disabled, loading, userStyle, userClass
  const {color, icon, iconClass, iconColor} = elState.attributes;
  // const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const badgeType = useMemo<Tbadge>(() => {
    const typ:Tbadge = {size: "default", status: "default"};
    (type || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["default", "success", "error", "processing", "warning"].indexOf(tt)) { typ.status = tt }
      else if (tt === "ribbon") { typ.ribbon = true }
      else if (~["start", "end"].indexOf(tt)) { typ.placement = tt }
      else if (tt === "small") { typ.size = tt }
      else if (tt === "dot") { typ.dot = true }
    });

    if (attributes?.size) { typ.size = attributes.size }
    if (attributes?.dot === "true") { typ.dot = true }
    if (attributes?.ribbon === "true") { typ.ribbon = true }
    return typ;
  }, [attributes, type]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass, elState?.disabled, permissions]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    return est;
  }, [elState?.userStyle, elState?.visible, color, theme, permissions]);

  const btext = useMemo<React.ReactNode>(() => {
    // eslint-disable-next-line max-len
    const txt = elState?.value || elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng];
    let rico:React.ReactNode = txt;
    if (icon) { rico = (<AppIcons key={key} icopath={icon} attrs={{className: iconClass, color: iconColor}} />) }
    return rico;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elState?.value, elState.attributes, attributes, lng]);

  const childArr = useMemo(() => (React.Children.toArray(children)), [children]);

  const textChild = useMemo<any>(() => {
    if (!childArr || childArr.length !== 1) return undefined;
    const ch = childArr[0];
    if (!React.isValidElement(ch)) return undefined;
    const chName = String(ch?.props?.attributes?.name || "").toLowerCase();
    return (chName === "text" ? ch : undefined);
  }, [childArr]);

  const textChildPath = useMemo<string>(() => {
    if (!textChild || !textChild?.props?.attributes?.fullPath) return fullPath;
    return textChild.props.attributes.fullPath;
  }, [textChild, fullPath]);

  const textChildState = useAppSelector(getElementState(rootAttributes.key, textChildPath));

  const statusText = useMemo<string | undefined>(() => {
    if (!textChild) return undefined;
    const cattrs = textChild?.props?.attributes || {};
    return textChildState?.value
      || textChildState?.attributes?.["title_" + lng]
      || textChildState?.attributes?.title
      || textChildState?.attributes?.["title_" + rlng]
      || textChildState?.attributes?.value
      || cattrs["title_" + lng]
      || cattrs.title
      || cattrs["title_" + rlng]
      || cattrs.value;
  }, [textChild, textChildState, lng, rlng]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);
  const statusValue = (elState?.status || badgeType.status);

  if (!badgeType.ribbon && badgeType.dot && statusValue && textChild) {
    return (
      <Badge
        key={key}
        className={clsu.join(" ")}
        style={styl}
        uk-tooltip={tooltip}
        // @ts-ignore
        status={statusValue}
        text={statusText}
      />
    );
  }

  if (badgeType.ribbon) {
    return (
      <Badge.Ribbon
        key={key} className={clsu.join(" ")} style={styl} color={color || "red"} text={btext} uk-tooltip={tooltip}
        placement={badgeType.placement}
      >
        {children}
      </Badge.Ribbon>
    );
  }

  return (
    <Badge
      key={key} className={clsu.join(" ")} style={styl} uk-tooltip={tooltip}
      size={badgeType.size} dot={badgeType.dot}
      count={btext}
      // @ts-ignore
      status={elState?.status || badgeType.status}
    >
      {children}
    </Badge>
  );
}

export default RZbadge;
/* eslint-enable @typescript-eslint/no-unused-vars */
